import dynamic from "next/dynamic"
import { useRouter } from "next/router"
import { ReactNode, useEffect, useState } from "react"
import { ErrorBoundary } from "react-error-boundary"

import ClientOnly from "@/components/ClientOnly"
import { IS_PROD } from "@/lib/constants"
import { url } from "@/lib/url"
import type { AdminBarProps } from "@/v2/components/AdminBar"
import { ErrorState } from "@/v2/components/ErrorState"
import FullStory from "@/v2/components/analytics/FullStory"
import { useOrgHomepageQuery } from "@/v2/graphql/public-generated-rq"
import { useOrgSlug } from "@/v2/hooks/router"
import { AuthRequired, PublicAuthProvider, useAuth } from "@/v2/providers/Auth"
import { twMerge } from "@lumen-energy/design-system"

import { CommandBar } from "../../../CommandBar"
import { HubspotScript } from "../../../HubspotScript"
import { Header } from "./Header"
import { Sidebar } from "./Sidebar"

const AdminBar = dynamic<AdminBarProps>(
  () => import("@/v2/components/AdminBar").then((mod) => mod.AdminBar),
  { ssr: false },
)

export interface AppLayoutProps {
  children: React.ReactNode
}

export const AppLayout = (props: AppLayoutProps) => {
  return (
    <PublicAuthProvider>
      <AuthRequired>
        <Layout {...props} />
      </AuthRequired>
    </PublicAuthProvider>
  )
}

const Layout = ({ children }: AppLayoutProps) => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { isAdmin } = useAuth()

  const router = useRouter()

  useEffect(() => {
    const closeSidebar = () => setSidebarOpen(false)
    router.events.on("routeChangeComplete", closeSidebar)
    return () => {
      router.events.off("routeChangeComplete", closeSidebar)
    }
  }, [router.events])

  return (
    <ClientOnly>
      <HubspotScript />
      {IS_PROD && isAdmin === false ? <FullStory /> : null}
      {isAdmin ? <AdminBar /> : null}
      {isAdmin ? <CommandBar /> : null}
      <OrgSlugChecker />
      <div className="min-h-screen w-full bg-grey-100 print:w-[1100px]">
        <Header
          onToggleSidebar={() => setSidebarOpen((v) => !v)}
          isSidebarOpen={sidebarOpen}
        />
        <div className="flex min-h-page">
          <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
          <main className="min-h-full w-full @container/page">
            <ErrorBoundary
              fallbackRender={({ error, resetErrorBoundary }) => (
                <ErrorState
                  title="Something went wrong"
                  subTitle="Try again or contact support@lumen.energy"
                  retryLabel="Try again"
                  onRetry={resetErrorBoundary}
                  rawError={error}
                />
              )}
            >
              {children}
            </ErrorBoundary>
          </main>
        </div>
      </div>
    </ClientOnly>
  )
}

export const PageContainer = ({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) => {
  return (
    <div className={twMerge("mx-auto h-full w-full max-w-screen-2xl", className)}>
      {children}
    </div>
  )
}

const OrgSlugChecker = () => {
  const orgSlug = useOrgSlug()
  const { data } = useOrgHomepageQuery({ orgSlug }, { enabled: Boolean(orgSlug) })
  const router = useRouter()

  useEffect(() => {
    const org = data?.orgBySlug
    if (orgSlug && org && org.slug !== orgSlug) {
      router.replace(url(["/[orgSlug]", { orgSlug: org.slug }]))
    }
  }, [data?.orgBySlug, orgSlug, router])

  return null
}
